import AddIcon from '@mui/icons-material/Add';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import {
  Box,
  Grid,
  styled,
  Switch,
  IconButton,
  Stack,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useEffect, useState } from 'react';
import { FormProvider, Controller, useForm } from 'react-hook-form';
import { updateManualTaxation, getManualTaxation } from 'wisestep-api';
import { WsTypography, WsIconEdit, WsTypoVariants, WsButton, Label } from 'wisestep-base';

import NoDataFoundCard from '../../../components/noDataFoundCard';
import { SettingsPageLayout } from '../../../components/SettingsPageLayout';
import { useWorkspaceContext } from '../../../context';
import { AtsCustomAPIAccess, useAtsCustomAPIAccess } from '../../../hooks/useAtsCustomAPIAccess';
import { success, warning } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { invoiceSettingsIndexOptions, TitleAndBreadCrumbs } from '../helper/InvocieSettingsHelper';

export interface ILabelWithStatus {
  paymentType: string;
  visibility: boolean;
  createdByName: string;
}

export interface ILabelTaxWithStatus {
  taxType: string;
  visibility: boolean;
  taxApplication: string;
  createdByName: string;
  rate: string;
  applyToTaxes?: string[];
}

export const HeaderBox = styled(Grid)(() => ({
  display: 'flex',
  flexDirection: 'row',
  minWidth: '100%',
}));

export const StyledSwitch = styled(Switch)(() => ({
  top: '0.5vw',
}));

interface IClientSettingsLayout {
  BreadCrumbListAndTitle: TitleAndBreadCrumbs;
  headerList: string[];
  itemsList?: ILabelWithStatus[];
  dataCallback?: (data: boolean, index: number) => void;
  handleAddButton?: () => void;
  handleEditButton?: (index: number) => void;
  handleViewButton?: (index: number) => void;
  addButtonText?: string;
  isInvoicePaymentType?: boolean;
  isInvoiceTaxType?: boolean;
  itemsTaxList?: ILabelTaxWithStatus[];
  backUrl: string;
}

const InvoiceSettingsLayout = ({
  BreadCrumbListAndTitle,
  headerList,
  dataCallback,
  itemsList,
  addButtonText,
  handleAddButton,
  handleEditButton,
  handleViewButton,
  isInvoicePaymentType,
  isInvoiceTaxType,
  itemsTaxList,
  backUrl,
}: IClientSettingsLayout) => {
  const methods = useForm();
  const { control } = methods;
  const itemXS = 12 / headerList.length;
  const [manualTaxation, setManualTaxation] = useState<boolean>();
  const setNotification = useNotificationStore((state) => state.setNotification);

  const workspaceContext = useWorkspaceContext();
  const { access: atsCustomAccessControl } = useAtsCustomAPIAccess([
    AtsCustomAPIAccess.MANAGE_CLIENT_SETTINGS,
  ]);

  const manageAccess = workspaceContext.owns || atsCustomAccessControl[0];

  useEffect(() => {
    getManualTaxation().then((response) => setManualTaxation(response.data.manual));
  }, []);

  return (
    <SettingsPageLayout
      title={BreadCrumbListAndTitle.Title}
      backUrl={backUrl}
      indexOptions={invoiceSettingsIndexOptions}
      breadCrumnbsList={BreadCrumbListAndTitle.BreadcrumbsList}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
        margin={2}
      >
        {isInvoiceTaxType && (
          <FormControlLabel
            control={
              <Checkbox
                checked={manualTaxation ?? false}
                onChange={async (e) => {
                  try {
                    const response = await updateManualTaxation(e.target.checked);
                    setManualTaxation(response.data.manual);
                    setNotification(success('Taxation Setting updated successfully!'));
                  } catch (error) {
                    setNotification(warning('Something Went Wrong, Please try again'));
                  }
                }}
              />
            }
            label="Select taxation manually while creating invoice"
          />
        )}
        {manageAccess && (
          <WsButton
            startIcon={<AddIcon />}
            variant="outlined"
            onClick={handleAddButton}
          >{`Add ${addButtonText}`}</WsButton>
        )}
      </Stack>
      <Box component="form" px={8} pt={2}>
        <FormProvider {...methods}>
          {isInvoicePaymentType && (
            <>
              <HeaderBox>
                {headerList?.map((header, index) => (
                  <Grid item xs={itemXS} key={index}>
                    <WsTypography variant={WsTypoVariants.SUB_TITLE}>{header}</WsTypography>
                  </Grid>
                ))}
              </HeaderBox>
              {isEmpty(itemsList) ? (
                <NoDataFoundCard text="No Data found, Please click add button" />
              ) : (
                <Grid container pt={6} spacing={2}>
                  {itemsList?.map((item, index) => (
                    <>
                      <Grid item xs={itemXS}>
                        <Label sx={{ fontWeight: 'bold' }}>{item.paymentType}</Label>
                      </Grid>
                      <Grid item xs={itemXS}>
                        <Controller
                          name="value"
                          control={control}
                          render={(props) => (
                            <StyledSwitch
                              defaultChecked={props.field.value ?? item.visibility}
                              onChange={(_event, checked) =>
                                dataCallback && dataCallback(checked, index)
                              }
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={itemXS}>
                        <Label sx={{ fontWeight: 'bold' }}>{item.createdByName}</Label>
                      </Grid>
                      <Grid item xs={itemXS}>
                        <IconButton onClick={() => handleEditButton && handleEditButton(index)}>
                          <WsIconEdit />
                        </IconButton>
                      </Grid>
                    </>
                  ))}
                </Grid>
              )}
            </>
          )}
          {isInvoiceTaxType && (
            <>
              <HeaderBox>
                {headerList?.map((header, index) => (
                  <Grid item xs={itemXS} key={index}>
                    <WsTypography variant={WsTypoVariants.SUB_TITLE}>{header}</WsTypography>
                  </Grid>
                ))}
              </HeaderBox>
              {isEmpty(itemsTaxList) ? (
                <NoDataFoundCard text="No Data found, Please click add button" />
              ) : (
                <Grid container pt={6} spacing={2}>
                  {itemsTaxList?.map((item, index) => (
                    <>
                      <Grid item xs={itemXS}>
                        <Label sx={{ fontWeight: 'bold' }}>{item.taxType}</Label>
                      </Grid>
                      <Grid item xs={itemXS}>
                        <Label sx={{ fontWeight: 'bold' }}>{item.rate}%</Label>
                      </Grid>
                      <Grid item xs={itemXS}>
                        <Label sx={{ fontWeight: 'bold' }}>{item.taxApplication}</Label>
                      </Grid>
                      <Grid item xs={itemXS}>
                        <Controller
                          name="value"
                          control={control}
                          render={(props) => (
                            <StyledSwitch
                              defaultChecked={props.field.value ?? item.visibility}
                              onChange={(_event, checked) =>
                                dataCallback && dataCallback(checked, index)
                              }
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={itemXS}>
                        <Label sx={{ fontWeight: 'bold' }}>{item.createdByName}</Label>
                      </Grid>
                      <Grid item xs={itemXS}>
                        <IconButton onClick={() => handleViewButton && handleViewButton(index)}>
                          <RemoveRedEyeOutlinedIcon />
                        </IconButton>
                      </Grid>
                      <Grid item xs={itemXS}>
                        <IconButton onClick={() => handleEditButton && handleEditButton(index)}>
                          <WsIconEdit />
                        </IconButton>
                      </Grid>
                    </>
                  ))}
                </Grid>
              )}
            </>
          )}
        </FormProvider>
      </Box>
    </SettingsPageLayout>
  );
};

export default InvoiceSettingsLayout;
